/* Biohackers Corner · small progressive enhancements.
   Everything here is optional: without JavaScript the pages still work. */

(() => {
  /* Horizontal scroller with previous / next buttons */
  if (!customElements.get('bc-scroller')) {
    class BcScroller extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.bc-scroller__track');
        if (!this.track) return;
        this.buttons = this.querySelectorAll('.bc-scroller__btn');
        this.buttons.forEach((button) =>
          button.addEventListener('click', () => this.scrollByPage(Number(button.dataset.dir) || 1))
        );
        this.update = this.update.bind(this);
        this.track.addEventListener('scroll', this.update, { passive: true });
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);
        this.update();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      scrollByPage(direction) {
        const item = this.track.querySelector('.bc-scroller__item');
        const step = item ? item.getBoundingClientRect().width : this.track.clientWidth * 0.8;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / (step + gap)));
        this.track.scrollBy({ left: direction * perPage * (step + gap), behavior: 'smooth' });
      }

      update() {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const scrollable = maxScroll > 4;
        this.classList.toggle('is-scrollable', scrollable);
        if (!this.buttons.length) return;
        this.buttons[0].disabled = this.track.scrollLeft <= 2;
        this.buttons[this.buttons.length - 1].disabled = this.track.scrollLeft >= maxScroll - 2;
      }
    }
    customElements.define('bc-scroller', BcScroller);
  }

  /* Size picker for the product spotlight: swaps variant id, price, image and link */
  if (!customElements.get('bc-size-picker')) {
    class BcSizePicker extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.bc-spotlight');
        this.addEventListener('change', (event) => {
          if (event.target.matches('input[type="radio"]')) this.select(event.target);
        });
      }

      select(input) {
        const idInput = this.querySelector('input[name="id"]');
        if (idInput) idInput.value = input.value;

        const price = this.querySelector('[data-bc-price]');
        if (price && input.dataset.price) price.textContent = input.dataset.price;

        const link = this.querySelector('[data-bc-url]');
        if (link && input.dataset.url) link.href = input.dataset.url;

        if (this.section && input.dataset.media !== undefined) {
          this.section.querySelectorAll('[data-bc-media-index]').forEach((frame) => {
            frame.classList.toggle('is-active', frame.dataset.bcMediaIndex === input.dataset.media);
          });
        }

        const submit = this.querySelector('[type="submit"]');
        if (submit) {
          const available = input.dataset.available === 'true';
          const label = submit.querySelector('span');
          submit.toggleAttribute('disabled', !available);
          if (label && window.variantStrings) {
            label.textContent = available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
          }
        }
      }
    }
    customElements.define('bc-size-picker', BcSizePicker);
  }

  /* Sticky add to cart bar on product pages */
  if (!customElements.get('bc-sticky-atc')) {
    class BcStickyAtc extends HTMLElement {
      connectedCallback() {
        const sectionId = this.dataset.section;
        this.mainButton = document.querySelector(`#product-form-${sectionId} [type="submit"]`);
        this.button = this.querySelector('[data-bc-sticky-submit]');
        this.priceTarget = this.querySelector('[data-bc-sticky-price]');
        this.priceSource = document.getElementById(`price-${sectionId}`);
        if (!this.mainButton || !this.button) return;

        this.button.addEventListener('click', () => {
          if (this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true') {
            this.mainButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          this.mainButton.click();
        });

        this.syncState();
        this.stateObserver = new MutationObserver(() => this.syncState());
        this.stateObserver.observe(this.mainButton, { attributes: true, childList: true, subtree: true, characterData: true });
        if (this.priceSource) this.stateObserver.observe(this.priceSource, { childList: true, subtree: true });

        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          const pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', pastButton);
          this.setAttribute('aria-hidden', pastButton ? 'false' : 'true');
          this.button.tabIndex = pastButton ? 0 : -1;
        });
        this.visibilityObserver.observe(this.mainButton);
      }

      disconnectedCallback() {
        this.stateObserver?.disconnect();
        this.visibilityObserver?.disconnect();
      }

      syncState() {
        const disabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
        const label = this.mainButton.querySelector('span');
        const buttonLabel = this.button.querySelector('span');
        if (buttonLabel && label) buttonLabel.textContent = label.textContent.trim();
        this.button.classList.toggle('is-disabled', disabled);
        if (this.priceTarget && this.priceSource) {
          const current = this.priceSource.querySelector('.price-item--last, .price-item--sale, .price-item--regular');
          if (current) this.priceTarget.textContent = current.textContent.trim();
        }
      }
    }
    customElements.define('bc-sticky-atc', BcStickyAtc);
  }

  /* Light / dark switch. The early script in theme.liquid sets html[data-theme]
     before paint; here we flip it, remember the choice and keep switches in sync. */
  const root = document.documentElement;
  const darkQuery = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;

  const syncThemeToggles = () => {
    const isDark = root.getAttribute('data-theme') === 'dark';
    document.querySelectorAll('[data-bc-theme-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-pressed', isDark ? 'true' : 'false');
    });
  };

  const setTheme = (theme, remember) => {
    root.classList.add('bc-theme-switching');
    root.setAttribute('data-theme', theme);
    if (remember) {
      try {
        localStorage.setItem('bc-theme', theme);
      } catch (e) {}
    }
    syncThemeToggles();
    window.setTimeout(() => root.classList.remove('bc-theme-switching'), 500);
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-bc-theme-toggle]');
    if (!toggle) return;
    setTheme(root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark', true);
  });

  /* Follow the device setting when the shop default is "auto" and the visitor has not chosen */
  darkQuery?.addEventListener('change', (event) => {
    if (root.getAttribute('data-theme-default') !== 'auto') return;
    let stored = null;
    try {
      stored = localStorage.getItem('bc-theme');
    } catch (e) {}
    if (!stored) setTheme(event.matches ? 'dark' : 'light', false);
  });

  syncThemeToggles();
  document.addEventListener('shopify:section:load', syncThemeToggles);

  /* Open the shop panel on hover for mouse users. A mouse click on "Shop" opens
     the all-products page; keyboard and touch still toggle the panel. */
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  document.querySelectorAll('.bc-mega').forEach((menu) => {
    const details = menu.querySelector('details');
    if (!details) return;
    const href = menu.dataset.href;
    details.querySelector('summary')?.addEventListener('click', (event) => {
      if (!href || !hoverQuery.matches || event.detail === 0) return;
      event.preventDefault();
      if (event.metaKey || event.ctrlKey) {
        window.open(href, '_blank', 'noopener');
      } else {
        window.location.href = href;
      }
    });
    let closeTimer;
    menu.addEventListener('mouseenter', () => {
      if (!hoverQuery.matches) return;
      clearTimeout(closeTimer);
      if (!details.open) {
        details.open = true;
        details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      }
    });
    menu.addEventListener('mouseleave', () => {
      if (!hoverQuery.matches) return;
      closeTimer = setTimeout(() => {
        details.open = false;
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      }, 180);
    });
  });
})();
